import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit';
import Raven from 'raven-js';
import { chirpClient } from '../../clients/chirpClient';
import { loginVerifyRpc } from '../../../__generated__/chirp/com/hubspot/portals/platform/app/rpc/AccessRpc';
import { previewLoginVerifyRpc } from '../../../__generated__/chirp/com/hubspot/commerce/buyer/portal/rpc/BuyerPreviewLoginRpc';
import { Metrics } from '../../utils/Metrics';
import { ErrorActions } from '../../constants/ErrorActions';
import { RequestStatus } from '../../constants/RequestStatus';
import { getPortalAccountId, getPortalInstanceId } from 'portals-platform-lib';
const initialState = {
  contact: null,
  isLoggedIn: false,
  requestStatus: RequestStatus.UNINITIALIZED,
  initialized: false,
  error: undefined
};
export const loginVerified = createAsyncThunk('auth/loginVerified', async () => {
  const startTime = performance.now();
  try {
    const response = await chirpClient.call(loginVerifyRpc, {
      request: {
        portalInstanceId: getPortalInstanceId(),
        accountId: getPortalAccountId()
      }
    });
    Metrics.timer('login-verify-duration').update(performance.now() - startTime);
    Metrics.counter('login-verify-success').increment();
    return response;
  } catch (error) {
    Metrics.timer('login-verify-duration').update(performance.now() - startTime);
    Metrics.counter('login-verify-failure').increment();
    Raven.captureException(error instanceof Error ? error : new Error(String(error)), {
      tags: {
        action: ErrorActions.LOGIN_VERIFIED
      }
    });
    throw error;
  }
});
export const previewLoginVerified = createAsyncThunk('auth/previewLoginVerified', async previewJwt => {
  try {
    const response = await chirpClient.call(previewLoginVerifyRpc, {
      request: {
        previewJwt
      }
    });
    if (!response.hasAccess) {
      var _response$denialReaso;
      throw new Error((_response$denialReaso = response.denialReason) !== null && _response$denialReaso !== void 0 ? _response$denialReaso : 'Preview access denied');
    }
    return response;
  } catch (error) {
    Raven.captureException(error instanceof Error ? error : new Error(String(error)), {
      tags: {
        action: ErrorActions.LOGIN_VERIFIED
      }
    });
    throw error;
  }
});
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(loginVerified.fulfilled, (state, action) => {
      state.initialized = true;
      state.isLoggedIn = true;
      state.contact = action.payload;
    }).addCase(loginVerified.rejected, state => {
      state.initialized = true;
      state.isLoggedIn = false;
    }).addCase(previewLoginVerified.fulfilled, state => {
      state.initialized = true;
      state.isLoggedIn = true;
    }).addCase(previewLoginVerified.rejected, state => {
      state.initialized = true;
      state.isLoggedIn = false;
    }).addMatcher(isAnyOf(loginVerified.pending, previewLoginVerified.pending), state => {
      state.requestStatus = RequestStatus.PENDING;
      state.error = undefined;
    }).addMatcher(isAnyOf(loginVerified.fulfilled, previewLoginVerified.fulfilled), state => {
      state.requestStatus = RequestStatus.SUCCEEDED;
    }).addMatcher(isAnyOf(loginVerified.rejected, previewLoginVerified.rejected), (state, action) => {
      var _action$error;
      state.requestStatus = RequestStatus.FAILED;
      state.error = (_action$error = action.error) === null || _action$error === void 0 ? void 0 : _action$error.message;
    });
  }
});
export default authSlice.reducer;